import {useEffect, useState} from 'react';
import {getRequest} from '@/Services/ApiService';
import APIURL from '@/api-url';
import {reportError} from '@/Services/Logger';
import {MIN_SEARCH_LENGTH} from '@/util/menu-links/searchIndex';

export interface NavContentItem {
    id: number;
    label: string;
    path: string;
}

interface WpListItem {
    id: number;
    slug: string;
    // This install is inconsistent: `use_case` returns the standard `{rendered}` object,
    // `posts` returns a plain string. Both shapes are handled.
    title?: string | {rendered?: string};
}

const titleOf = (row: WpListItem): string => (typeof row.title === 'string' ? row.title : row.title?.rendered ?? row.slug);

const PER_PAGE = 4;

const SEARCH_PAGE_SIZE = 12;
const DEBOUNCE_MS = 250;
const FIELDS = 'id,slug,title';
const order = (perPage: number) => `orderby=modified&order=desc&per_page=${perPage}&_fields=${FIELDS}`;
const ORDER = order(PER_PAGE);

let latestCache: {useCases: NavContentItem[]; blogs: NavContentItem[]} | null = null;

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
    amp: '&',
    quot: '"',
    apos: "'",
    rsquo: "'",
    nbsp: ' '
};

const MAX_CODE_POINT = 0x10ffff;

const ENTITY_PATTERN = /&(?:#(\d+)|([a-z]+));/gi;

const decodeEntities = (raw: string): string =>
    raw.replace(ENTITY_PATTERN, (match, code: string | undefined, name: string | undefined) => {
        if (code !== undefined) {
            const point = Number(code);

            return Number.isFinite(point) && point >= 0 && point <= MAX_CODE_POINT ? String.fromCodePoint(point) : match;
        }
        return (name && NAMED_ENTITIES[name.toLowerCase()]) ?? match;
    });

const toItems = (rows: unknown, basePath: string): NavContentItem[] =>
    Array.isArray(rows)
        ? (rows as WpListItem[])
              .filter((row) => row?.slug)
              .map((row) => ({
                  id: row.id,
                  label: decodeEntities(titleOf(row)),
                  path: `${basePath}/${row.slug}`
              }))
        : [];

const WORD_BOUNDARY = String.raw`\b`;

const escapeRegExp = (raw: string): string => raw.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

export const titleMatchesTerm = (title: string, term: string, exact: boolean): boolean =>
    term
        .split(/\s+/)
        .filter(Boolean)
        .every((word) => new RegExp(`${WORD_BOUNDARY}${escapeRegExp(word)}${exact ? WORD_BOUNDARY : ''}`, 'i').test(title));

const fetchPage = async (endpoint: string, basePath: string, query: string, signal: AbortSignal): Promise<NavContentItem[]> =>
    toItems(await getRequest(`${endpoint}?${query}`, signal), basePath);

const fetchList = async (endpoint: string, basePath: string, term: string, signal: AbortSignal): Promise<NavContentItem[]> => {
    if (!term) return fetchPage(endpoint, basePath, ORDER, signal);

    const byTitle = await fetchPage(
        endpoint,
        basePath,
        `${order(SEARCH_PAGE_SIZE)}&search=${encodeURIComponent(term)}&search_columns=post_title`,
        signal
    );

    const exact = byTitle.filter((item) => titleMatchesTerm(item.label, term, true));
    const matched = exact.length > 0 ? exact : byTitle.filter((item) => titleMatchesTerm(item.label, term, false));

    return matched.slice(0, PER_PAGE);
};

export function useNavSearchContent(query: string, enabled: boolean) {
    const term = query.trim();
    // Same threshold as the static nav index — below it we show the default latest lists.
    const effectiveTerm = term.length >= MIN_SEARCH_LENGTH ? term : '';

    const [useCases, setUseCases] = useState<NavContentItem[]>(() => latestCache?.useCases ?? []);
    const [blogs, setBlogs] = useState<NavContentItem[]>(() => latestCache?.blogs ?? []);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;

        if (!effectiveTerm && latestCache) {
            setUseCases(latestCache.useCases);
            setBlogs(latestCache.blogs);
            return undefined;
        }

        const controller = new AbortController();
        const timer = setTimeout(
            () => {
                setLoading(true);
                Promise.all([
                    fetchList(APIURL.USE_CASE, '/use-cases', effectiveTerm, controller.signal),
                    fetchList(APIURL.BLOGS_LISTING, '/blog', effectiveTerm, controller.signal)
                ])
                    .then(([nextUseCases, nextBlogs]) => {
                        if (controller.signal.aborted) return;
                        setUseCases(nextUseCases);
                        setBlogs(nextBlogs);
                        if (!effectiveTerm) latestCache = {useCases: nextUseCases, blogs: nextBlogs};
                    })
                    .catch((error) => {
                        if (controller.signal.aborted) return;
                        // A WordPress hiccup must not break the nav — the static service-page
                        // results still render, these two sections just stay empty.
                        reportError(error, {context: 'useNavSearchContent', query: effectiveTerm});
                        setUseCases([]);
                        setBlogs([]);
                    })
                    .finally(() => {
                        if (!controller.signal.aborted) setLoading(false);
                    });
                // No debounce on the initial (empty-term) load — that one fires as soon as the
                // field opens, and there is nothing to wait for.
            },
            effectiveTerm ? DEBOUNCE_MS : 0
        );

        return () => {
            clearTimeout(timer);
            controller.abort();
            // The aborted run's `finally` deliberately skips setLoading(false) (it must not
            // write state for a request that no longer owns the field). Clearing the flag
            // here is what stops `loading` sticking true when the panel closes mid-flight.
            setLoading(false);
        };
    }, [effectiveTerm, enabled]);

    return {useCases, blogs, loading};
}
